import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action, toJS } from 'mobx';
import { Alert, Icon, Intent } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import { formatTime } from 'helpers/time';
import TechnicianPicker from 'components/TechnicianPicker';
import PDFViewer from 'components/WorkReport/PDFViewer';
import SendWorkReportModal from 'components/WorkReport/SendWorkReportModal';
import AddInvoiceNumberModal from 'pages/work-reports/AddInvoiceNumberModal';
import AddProductForWorkReportModal from 'components/WorkReport/AddProductForWorkReportModal';
import AddEquipmentForWorkReportModal from 'components/Equipment/AddEquipmentForWorkReportModal';
import AddFormModal from 'components/WorkReport/AddFormModal';
import FormSubmissionModal from 'components/WorkReport/FormSubmissionModal';
import DocModel, { reason } from 'components/DocEditor/DocModel';
import LineItems from 'components/DocEditor/LineItems';
import RichText from 'components/DocEditor/RichText';
import {
  Toolbar, ToolButton, MoreMenu, StatusSelect, Letterhead, DocTitle, ClientBlock, Meta, LiveTotals,
  SheetSection, SideCard, Signatures, FilesCard, SaveBar, SidePanel, MarkupNote, BarFigures, Breakdown, PrivateNotes,
} from 'components/DocEditor/DocParts';

// A work order (pages/work-reports/show.js), edited as the document it is:
// a centred sheet with the letterhead, who it's for, the job, what was done
// (labour, materials, other) and its total, stamped with its status; the
// controls in a strip above it; files, forms and equipment below it; and a
// save bar that appears when there are unsaved changes.

export const WORK_ORDER_STATUSES = [
  { value: 'draft', label: 'Draft', tone: 'grey' },
  { value: 'submitted', label: 'Submitted', tone: 'blue' },
  { value: 'reviewed', label: 'Reviewed', tone: 'purple' },
  { value: 'invoice', label: 'Invoiced', tone: 'green' },
];

// The server answers "invoiced"; the event that gets there is "invoice".
const statusValue = (status) => (status === 'invoiced' ? 'invoice' : status);
const isInvoiced = (status) => status === 'invoiced' || status === 'invoice';

@inject('store', 'viewStore', 'workReportStore', 'equipmentStore', 'formStore', 'formSubmissionStore') @observer
class WorkOrderEditor extends React.Component {
  @observable sendOpen = false;
  @observable invoiceNumberOpen = false;
  @observable pickerType = null;
  @observable equipmentOpen = false;
  @observable formsOpen = false;
  @observable openForm = null;
  @observable removingEquipment = null;
  @observable busy = null; // which action is running

  constructor(props) {
    super(props);
    const { workReportStore, viewStore } = props;
    const store = workReportStore;
    const id = () => store.workReport.object.id;
    this.doc = new DocModel({
      kind: 'workOrder',
      noun: 'work order',
      features: { markup: true, override: true, materialOrders: true, taxes: false },
      fields: ['po_number', 'description', 'invoice_number', 'private_notes'],
      getRecord: () => store.workReport.object,
      saveRecord: () => store.update(),
      reloadRecord: () => store.loadWorkReport({ uuid: id() }),
      truckCharge: () => viewStore.currentUser && viewStore.currentUser.attributes && viewStore.currentUser.attributes.truck_charge,
    });
    Object.assign(this.doc, {
      deleteLine: (item, deleteTimeLog) => store.deleteLineItem(item, deleteTimeLog),
      addBlankLine: (kind) => store.addBlankLineItem(kind, id()),
      openPicker: (type) => { this.pickerType = type; },
      orderMaterial: (item) => this.doc.withSaved(() => store.addMaterialLineItem(item)),
      materialOrders: () => store.materialLineItem,
      loadMaterialOrders: (item) => {
        viewStore.materialLineItemSearchById.where.line_item_id = item.id;
        store.loadMaterialLineItemById();
      },
      uploadFiles: (files) => store.uploadWorkReportAttachment(files, id()).then(() => store.update()),
      removeFile: (file) => store.removeAttachment({ id: file.id }, id()).then(() => store.update()),
    });
  }

  componentWillUnmount() {
    this.doc.dispose();
  }

  get attributes() {
    return this.props.workReportStore.workReport.object.attributes;
  }

  run(name, work, success, failure) {
    this.busy = name;
    return work()
      .then((response) => { if (success) success(response); return response; })
      .catch((error) => { Toast.show({ message: reason(error, failure), intent: Intent.DANGER }); })
      .finally(action(() => { this.busy = null; }));
  }

  changeStatus = (value) => {
    const { workReportStore } = this.props;
    const status = WORK_ORDER_STATUSES.find((option) => option.value === value);
    this.run('status',
      () => this.doc.withSaved(() => workReportStore.changeStatus(workReportStore.workReport.object, value)),
      (response) => {
        Toast.show({ message: `Marked as ${status ? status.label : value}.`, intent: Intent.SUCCESS });
        const saved = response && response.data && response.data.data;
        if (saved && isInvoiced(saved.attributes.status)) this.invoiceNumberOpen = true;
      },
      "Couldn't change the status. Try again.");
  };

  changeTechnician = (selections) => {
    const { workReportStore } = this.props;
    const record = workReportStore.workReport.object;
    const current = record.attributes.created_by && record.attributes.created_by.id;
    const next = selections.map((option) => option.value).find((value) => value !== current);
    if (!next) return;
    this.run('technician',
      () => this.doc.withSaved(() => workReportStore.changeTechnician(record, next)),
      (response) => {
        const person = response && response.data && response.data.data.attributes.created_by;
        Toast.show({ message: person ? `Technician changed to ${person.full_name}.` : 'Technician changed.', intent: Intent.SUCCESS });
      },
      "Couldn't change the technician. Try again.");
  };

  generateInvoice = () => {
    const { workReportStore, viewStore } = this.props;
    this.run('invoice',
      () => this.doc.withSaved(() => workReportStore.generateInvoice(workReportStore.workReport.object)),
      (response) => {
        Toast.show({ message: 'Invoice created.', intent: Intent.SUCCESS });
        viewStore.ShowInvoice({ uuid: response.data.data.id });
      },
      "Couldn't create the invoice. Try again.");
  };

  unlinkInvoice = () => {
    const { workReportStore } = this.props;
    this.run('unlink',
      () => this.doc.withSaved(() => workReportStore.unlinkInvoice(workReportStore.workReport.object)),
      () => { Toast.show({ message: 'Invoice unlinked.', intent: Intent.SUCCESS }); },
      "Couldn't unlink the invoice. Try again.");
  };

  // Sending and the PDF use the saved work order: save changes first.
  afterSave(name, next) {
    this.run(name, () => this.doc.withSaved(() => undefined), () => next(), "Couldn't save your changes. Try again.");
  }

  downloadPdf = () => {
    this.run('pdf', () => this.doc.withSaved(() => this.props.workReportStore.downloadWorkOrderPDF()), null, "Couldn't download the PDF. Try again.");
  };

  openEquipment = () => {
    // Adding equipment reloads the work order; save first so nothing's lost.
    this.run('equipment', () => this.doc.withSaved(() => undefined), () => { this.equipmentOpen = true; }, "Couldn't save your changes. Try again.");
  };

  removeEquipment = () => {
    const equipment = this.removingEquipment;
    this.removingEquipment = null;
    const { equipmentStore, workReportStore } = this.props;
    this.run('equipment',
      () => this.doc.withSaved(() => equipmentStore.deleteServicedEquipment(equipment.serviced_equipment_id, workReportStore.workReport.object.id)),
      () => { Toast.show({ message: `${equipment.kind || equipment.make || 'Equipment'} taken off this work order.`, intent: Intent.SUCCESS }); },
      "Couldn't take the equipment off. Try again.");
  };

  openForms = () => {
    this.formsOpen = true; // the panel loads and searches the forms itself
  };

  showForm = (form) => {
    this.props.formSubmissionStore.getFormSubmission(form);
    this.openForm = form;
  };

  renderToolbar() {
    const a = this.attributes;
    const invoiced = isInvoiced(a.status);
    return (
      <Toolbar
        actions={(
          <>
            <MoreMenu
              items={[
                !invoiced && { text: 'Create invoice', icon: 'document-share', onClick: this.generateInvoice, disabled: this.busy === 'invoice' },
                invoiced && a.invoice_id && { text: 'View invoice', icon: 'eye-open', onClick: () => this.props.viewStore.ShowInvoice({ uuid: a.invoice_id }) },
                invoiced && { text: 'Unlink invoice', icon: 'disable', onClick: this.unlinkInvoice, disabled: this.busy === 'unlink' },
                { text: a.invoice_number ? 'Change invoice number' : 'Add invoice number', icon: 'numerical', onClick: () => { this.invoiceNumberOpen = true; } },
                { divider: true },
                { text: this.busy === 'pdf' ? 'Downloading…' : 'Download PDF', icon: 'download', onClick: this.downloadPdf, disabled: this.busy === 'pdf' },
              ]}
            />
            <ToolButton icon="eye-open" busy={this.busy === 'view'} onClick={() => this.afterSave('view', () => { this.props.workReportStore.showPdfViewer = true; })}>View PDF</ToolButton>
            <ToolButton icon="envelope" primary busy={this.busy === 'send'} onClick={() => this.afterSave('send', () => { this.sendOpen = true; })}>Send work order</ToolButton>
          </>
        )}
      >
        <StatusSelect value={statusValue(a.status)} statuses={WORK_ORDER_STATUSES} disabled={this.busy === 'status'} onChange={this.changeStatus} />
      </Toolbar>
    );
  }

  renderParty() {
    const { viewStore } = this.props;
    const a = this.attributes;
    const client = a.client || {};
    const address = (a.current_location && a.current_location.full_address) || (a.location && a.location.full_address);
    const job = a.job && a.job.id ? a.job : null;
    const tech = a.created_by;
    return (
      <div className="de-parties">
        <ClientBlock label="Client" client={client} address={address} href={client.id ? `/dashboard/clients/${client.id}` : undefined} />
        <div className="de-meta">
          {job && (
            <Meta label="Job">
              <a className="de-strong-link" href={`/dashboard/jobs/${job.id}/${(viewStore.jobTab && viewStore.jobTab.section) || 'details'}`}>#{job.number}</a>
              {job.title && <span className="de-muted">{job.title}</span>}
              {job.service_type_name && <span className="de-muted">{job.service_type_name}</span>}
            </Meta>
          )}
          <Meta label="Technician">
            <div className="de-picker">
              <TechnicianPicker
                fill="true"
                selectedOptions={tech ? toJS([{ id: tech.id, label: tech.full_name, value: tech.id, selected: true }]) : []}
                onChange={this.changeTechnician}
                includeAdmins={true}
                hideHeading={true}
              />
            </div>
            {tech && tech.phone && <a className="de-contact" href={`tel:${tech.phone}`}>{tech.phone}</a>}
            {tech && tech.email && <a className="de-contact" href={`mailto:${tech.email}`}>{tech.email}</a>}
          </Meta>
          <Meta label="PO number" htmlFor="de-po">
            <input id="de-po" className="de-field" placeholder="None" value={a.po_number || ''} onChange={(event) => { a.po_number = event.target.value; }} />
          </Meta>
          {a.invoice_number && (
            <Meta label="Invoice number">
              <button type="button" className="de-link-button" onClick={() => { this.invoiceNumberOpen = true; }}>{a.invoice_number}</button>
            </Meta>
          )}
        </div>
      </div>
    );
  }

  renderEquipment() {
    const equipment = this.attributes.equipments || [];
    return (
      <SideCard
        title="Equipment"
        count={equipment.length}
        action={<ToolButton icon="wrench" busy={this.busy === 'equipment'} onClick={this.openEquipment}>Add equipment</ToolButton>}
      >
        {equipment.length === 0 ? (
          <p className="de-muted">No equipment yet. Add the units serviced on this visit.</p>
        ) : (
          <ul className="de-equipment">
            {equipment.map((unit) => {
              const photo = unit.images && unit.images[0] && unit.images[0].asset;
              const makeModel = [unit.make, unit.model].filter(Boolean).join(' · ');
              const name = unit.kind || makeModel || 'Equipment';
              return (
                <li key={unit.serviced_equipment_id || unit.id} className="de-equipment__item">
                  <span className={`de-equipment__photo${photo ? ' has-photo' : ''}`} aria-hidden="true">
                    {photo ? <img src={photo.thumb && photo.thumb.url ? photo.thumb.url : photo.url} alt="" /> : <Icon icon="wrench" iconSize={16} />}
                  </span>
                  <div className="de-equipment__text">
                    {unit.id ? <a className="de-equipment__name" href={`/dashboard/equipments/${unit.id}/show`}>{name}</a> : <strong className="de-equipment__name">{name}</strong>}
                    {unit.kind && makeModel && <span>{makeModel}</span>}
                    {unit.description && <span className="de-equipment__description" title={unit.description}>{unit.description}</span>}
                    {unit.serial_number && <span className="de-equipment__serial">S/N {unit.serial_number}</span>}
                    {unit.physical_location && (
                      <span className="de-equipment__where"><Icon icon="map-marker" iconSize={11} />{unit.physical_location}</span>
                    )}
                  </div>
                  <button type="button" className="de-icon-button de-icon-button--danger de-equipment__remove" aria-label={`Take ${name} off this work order`} title="Take off this work order" onClick={() => { this.removingEquipment = unit; }}>
                    <Icon icon="cross" iconSize={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </SideCard>
    );
  }

  renderForms() {
    const forms = this.attributes.forms || [];
    const { formSubmissionStore } = this.props;
    return (
      <SideCard title="Forms" count={forms.length} action={<ToolButton icon="form" onClick={this.openForms}>Add form</ToolButton>}>
        {forms.length === 0 ? (
          <p className="de-muted">No forms yet. Add a checklist or inspection form to fill in.</p>
        ) : (
          <ul className="de-forms">
            {forms.map((form) => (
              <li key={form.id} className="de-form">
                <button type="button" className="de-form__open" onClick={() => this.showForm(form)}>
                  <Icon icon="form" iconSize={14} />
                  <span>
                    <strong>{form.name}</strong>
                    <span className="de-muted">{[form.created_by_name, form.created_at && formatTime(form.created_at)].filter(Boolean).join(', ')}</span>
                  </span>
                </button>
                <button type="button" className="de-icon-button" aria-label={`Download ${form.name}`} title="Download PDF" onClick={() => formSubmissionStore.downloadFormSubmissionPDF(form)}>
                  <Icon icon="download" iconSize={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </SideCard>
    );
  }

  render() {
    const doc = this.doc;
    const a = this.attributes;
    const status = WORK_ORDER_STATUSES.find((option) => option.value === statusValue(a.status));
    return (
      <div className="de de--work-order">
        {this.renderToolbar()}

        <article className="de-sheet" aria-labelledby="de-title">
          <Letterhead tenant={a.tenant} />
          <DocTitle kind="Work order" number={a.number} stamp={status} />
          {this.renderParty()}

          <SheetSection title="Description" className="de-section--intro">
            <RichText label="Description" value={a.description || ''} placeholder="What was the problem, and what was done?" onChange={(html) => { a.description = html; }} />
          </SheetSection>

          <LineItems doc={doc} />

          <LiveTotals
            doc={doc}
            build={(totals) => ({
              note: <><Breakdown doc={doc} /><MarkupNote totals={totals} /></>,
              rows: [
                { label: 'Total', value: totals.grand, strong: true },
              ],
            })}
          />

          <SheetSection title="Signatures" className="de-section--signatures">
            <Signatures signatures={a.signatures} />
          </SheetSection>

          <PrivateNotes attributes={a} RichTextEditor={RichText} />
        </article>

        <SidePanel kind="workOrder">
          <FilesCard title="Files" count={(a.images || []).filter((file) => file && file.asset && file.asset.url).length} doc={doc} images={a.images} />
          {this.renderForms()}
          {this.renderEquipment()}
        </SidePanel>

        <SaveBar
          doc={doc}
          figures={(
            <BarFigures
              doc={doc}
              build={(totals) => [
                { label: 'Gross markup', text: totals.markupPercent === null ? '–' : `${totals.markupPercent}%` },
                { label: 'Gross margin', value: totals.margin },
                { label: 'Total', value: totals.grand, strong: true },
              ]}
            />
          )}
        />

        <PDFViewer />
        <SendWorkReportModal isOpen={this.sendOpen} onClose={() => { this.sendOpen = false; }} onSuccess={() => { this.sendOpen = false; }} />
        <AddInvoiceNumberModal isOpen={this.invoiceNumberOpen} onClose={() => { this.invoiceNumberOpen = false; }} onSuccess={() => { this.invoiceNumberOpen = false; }} />
        <AddProductForWorkReportModal
          isOpen={Boolean(this.pickerType)}
          type={this.pickerType || 'Service'}
          itemType={this.pickerType === 'Service' ? 'Labor' : 'Material'}
          workDetail={this.props.workReportStore.workReport.object}
          onClose={() => { this.pickerType = null; }}
        />
        <AddEquipmentForWorkReportModal
          isOpen={this.equipmentOpen}
          workDetail={this.props.workReportStore.workReport.object}
          onClose={() => { this.equipmentOpen = false; }}
        />
        <AddFormModal isOpen={this.formsOpen} onClose={() => { this.formsOpen = false; }} onSuccess={() => { this.formsOpen = false; }} />
        {this.openForm && (
          <FormSubmissionModal isOpen formSubmission={this.openForm} onClose={() => { this.openForm = null; }} />
        )}
        <Alert
          isOpen={Boolean(this.removingEquipment)}
          icon="cross"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Take it off"
          onCancel={() => { this.removingEquipment = null; }}
          onConfirm={this.removeEquipment}
        >
          <strong>Take {this.removingEquipment ? (this.removingEquipment.kind || 'this equipment') : ''} off this work order?</strong>
          <p>The equipment stays on the client's record; it's just no longer listed as serviced here.</p>
        </Alert>
      </div>
    );
  }
}

export default WorkOrderEditor;
